import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@repo/ui-primitives/dialog';
import { Input } from '@repo/ui-primitives/input';
import { Separator } from '@repo/ui-primitives/separator';
import { Link, useNavigate } from '@tanstack/react-router';
import Fuse from 'fuse.js';
import { Folder, Package, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { STORAGE_KEYS } from '@repo/constants';
import { useApplicationList, useProjectList } from '@repo/domains-organization';
import { useTypedParams } from '@repo/routes';
import { useLocalStorage } from './use-local-storage';
export function SearchModal(param) {
    let { open, onOpenChange, initialQuery = '' } = param;
    const [savedSearchQuery, setSavedSearchQuery] = useLocalStorage(STORAGE_KEYS.SEARCH_QUERY, '');
    const [searchQuery, setSearchQuery] = useState(savedSearchQuery || initialQuery);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const params = useTypedParams();
    const { projectList } = useProjectList({
        organization: params.organization
    });
    const { applicationList } = useApplicationList({
        organization: params.organization,
        queryParams: searchQuery.trim() ? {
            name: searchQuery
        } : undefined
    });
    const organization = params.organization;
    const navigate = useNavigate();
    const inputRef = useRef(null);
    useEffect(()=>{
        if (open && inputRef.current) {
            // Wait for dialog animation and focus management to complete
            setTimeout(()=>{
                var _inputRef_current, _inputRef_current1;
                (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
                (_inputRef_current1 = inputRef.current) === null || _inputRef_current1 === void 0 ? void 0 : _inputRef_current1.select();
            }, 150);
        }
    }, [
        open
    ]);
    const handleFocus = (e)=>{
        // Select text when input receives focus
        e.target.select();
    };
    useEffect(()=>{
        if (initialQuery) {
            setSearchQuery(initialQuery);
        }
    }, [
        initialQuery
    ]);
    useEffect(()=>{
        setSavedSearchQuery(searchQuery);
    }, [
        searchQuery,
        setSavedSearchQuery
    ]);
    useEffect(()=>{
        setSelectedIndex(0);
    }, [
        searchQuery
    ]);
    const filteredProjects = useMemo(()=>{
        if (!searchQuery.trim() || !projectList) {
            return [];
        }
        const fuse = new Fuse(projectList, {
            keys: [
                'name',
                'displayName'
            ],
            threshold: 0.3
        });
        if (!organization) return [];
        return fuse.search(searchQuery).map((result)=>({
                id: result.item.id,
                type: 'project',
                href: "/org/".concat(organization, "/").concat(result.item.name),
                project: result.item
            }));
    }, [
        projectList,
        searchQuery,
        organization
    ]);
    const filteredApplications = useMemo(()=>{
        if (!searchQuery.trim()) {
            return [];
        }
        return applicationList.map((app)=>({
                id: "".concat(app.project.id, "-").concat(app.id),
                type: 'application',
                href: "/org/".concat(organization, "/").concat(app.project.name, "/").concat(app.name, "/"),
                project: app.project,
                application: {
                    id: app.id,
                    name: app.name,
                    displayName: app.name
                }
            }));
    }, [
        applicationList,
        searchQuery,
        organization
    ]);
    const allResults = useMemo(()=>{
        return [
            ...filteredProjects,
            ...filteredApplications
        ];
    }, [
        filteredProjects,
        filteredApplications
    ]);
    const handleKeyDown = useCallback((e)=>{
        switch(e.key){
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev)=>Math.min(prev + 1, allResults.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev)=>Math.max(prev - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (allResults[selectedIndex]) {
                    navigate({
                        to: allResults[selectedIndex].href
                    }).catch((error)=>{
                        if (error instanceof Error) {
                            throw new Error("Navigation error: ".concat(error.message));
                        }
                        throw new Error('Unknown navigation error');
                    });
                    onOpenChange(false);
                }
                break;
            case 'Escape':
                e.preventDefault();
                onOpenChange(false);
                break;
        }
    }, [
        allResults,
        selectedIndex,
        navigate,
        onOpenChange
    ]);
    const handleLinkClick = ()=>{
        onOpenChange(false);
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "max-w-2xl h-[600px] overflow-hidden flex flex-col border-[var(--border)]",
            showCloseButton: false,
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    className: "sr-only",
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Search Projects and Applications"
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            children: "Search for projects and applications in your organization"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-2 pt-6 text-gray-25",
                    children: [
                        /*#__PURE__*/ _jsx(Search, {
                            className: "h-5 w-5 text-muted-foreground"
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            ref: inputRef,
                            value: searchQuery,
                            onChange: (e)=>{
                                setSearchQuery(e.target.value);
                            },
                            onKeyDown: handleKeyDown,
                            onFocus: handleFocus,
                            placeholder: "Search projects and applications...",
                            //don't add className here
                            autoFocus: true
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "overflow-y-auto flex-1 -mx-6 px-6 scrollbar-gutter-stable [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/80 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground",
                    children: [
                        searchQuery.trim() && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                filteredProjects.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "mb-6",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-sm font-semibold text-muted-foreground mb-3",
                                            children: "Projects"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "space-y-1",
                                            children: filteredProjects.map((result, index)=>{
                                                const isSelected = selectedIndex === index;
                                                return /*#__PURE__*/ _jsxs(Link, {
                                                    to: result.href,
                                                    onClick: handleLinkClick,
                                                    className: "flex items-center gap-3 px-3 py-2 rounded-md text-sm ".concat(isSelected ? 'bg-accent' : 'hover:bg-accent'),
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Folder, {
                                                            className: "h-4 w-4 stroke-turquoise-500"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "font-medium text-accent-foreground",
                                                            children: result.project.displayName
                                                        })
                                                    ]
                                                }, result.id);
                                            })
                                        })
                                    ]
                                }),
                                filteredProjects.length > 0 && filteredApplications.length > 0 && /*#__PURE__*/ _jsx(Separator, {
                                    className: "mb-6"
                                }),
                                filteredApplications.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-sm font-semibold text-muted-foreground mb-3",
                                            children: "Applications"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "space-y-1",
                                            children: filteredApplications.map((result, index)=>{
                                                const globalIndex = filteredProjects.length + index;
                                                const isSelected = selectedIndex === globalIndex;
                                                return /*#__PURE__*/ _jsxs(Link, {
                                                    to: result.href,
                                                    onClick: handleLinkClick,
                                                    className: "flex items-center gap-3 px-3 py-2 rounded-md text-sm ".concat(isSelected ? 'bg-accent' : 'hover:bg-accent'),
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Package, {
                                                            className: "h-4 w-4 stroke-turquoise-500"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center gap-2",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-gray-25",
                                                                    children: result.project.displayName
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-muted-foreground",
                                                                    children: "/"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "font-medium text-gray-25",
                                                                    children: result.application.name
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                }, result.id);
                                            })
                                        })
                                    ]
                                }),
                                filteredProjects.length === 0 && filteredApplications.length === 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "text-center py-8 text-muted-foreground",
                                    children: [
                                        'No results found for "',
                                        searchQuery,
                                        '"'
                                    ]
                                })
                            ]
                        }),
                        !searchQuery.trim() && /*#__PURE__*/ _jsx("div", {
                            className: "text-center py-8 text-muted-foreground",
                            children: "Start typing to search projects and applications"
                        })
                    ]
                })
            ]
        })
    });
}
